import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Phone, IdCard, Mail, MapPin, FileText, Upload, CalendarClock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/api";

const EMPTY = { name: "", position: "", phone: "", email: "", address: "", postcode: "", licence_number: "", licence_country: "", licence_expiry: "", points: "", cpc_expiry: "", tacho_expiry: "", last_check_date: "", dob: "", ni_number: "", notes: "" };

const FIELDS = [
  ["Name *", "name", "text", "driver-name-input"],
  ["Position", "position", "text", "driver-position-input"],
  ["Phone number", "phone", "text", "driver-phone-input"],
  ["Email address", "email", "text", "driver-email-input"],
  ["Address", "address", "text", "driver-address-input"],
  ["Postcode", "postcode", "text", "driver-postcode-input"],
  ["Licence number", "licence_number", "text", "driver-licence-input"],
  ["Licence country", "licence_country", "text", "driver-licence-country-input"],
  ["Licence expiry", "licence_expiry", "date", "driver-licence-expiry-input"],
  ["Endorsement points", "points", "number", "driver-points-input"],
  ["CPC expiry", "cpc_expiry", "date", "driver-cpc-input"],
  ["Tacho expiry", "tacho_expiry", "date", "driver-tacho-input"],
  ["Last licence check", "last_check_date", "date", "driver-last-check-input"],
  ["Date of birth", "dob", "date", "driver-dob-input"],
  ["NI number", "ni_number", "text", "driver-ni-input"],
  ["Notes", "notes", "text", "driver-notes-input"],
];

const expiryClass = (val) => {
  if (!val) return "";
  const days = Math.floor((new Date(val) - new Date()) / 86400000);
  if (days < 0) return "text-[#FF3B30] font-bold";
  if (days <= 60) return "text-amber-600 dark:text-[#FFD60A] font-bold";
  return "";
};

export default function DriversPage() {
  const { canEdit } = useAuth();
  const editable = canEdit("drivers");
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [licFile, setLicFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/drivers").then((r) => setDrivers(r.data));
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const openAdd = () => { setEditId(null); setForm(EMPTY); setLicFile(null); setOpen(true); };
  const openEdit = (d) => {
    setEditId(d.id);
    const f = { ...EMPTY };
    Object.keys(EMPTY).forEach((k) => { f[k] = d[k] ?? ""; });
    setLicFile(null);
    setForm(f);
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Driver name is required");
    setSaving(true);
    try {
      const payload = { ...form, points: form.points === "" ? null : Number(form.points) };
      let driverId = editId;
      if (editId) {
        await api.put(`/drivers/${editId}`, payload);
      } else {
        const { data } = await api.post("/drivers", payload);
        driverId = data.id;
      }
      if (licFile) {
        const fd = new FormData();
        fd.append("file", licFile);
        await api.post(`/drivers/${driverId}/licence`, fd);
      }
      toast.success(editId ? `Driver ${form.name} updated` : `Driver ${form.name} added`);
      setOpen(false);
      setForm(EMPTY);
      setEditId(null);
      setLicFile(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const uploadLicence = async (driverId, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/drivers/${driverId}/licence`, fd);
      toast.success("Driving licence uploaded");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const viewLicence = async (d) => {
    try {
      const res = await api.get(`/drivers/${d.id}/licence`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] || "application/pdf" })), "_blank");
    } catch (e) {
      toast.error("No licence uploaded");
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Remove driver ${d.name}? Their vehicles will become unassigned.`)) return;
    await api.delete(`/drivers/${d.id}`);
    toast.success("Driver removed");
    load();
  };

  const vehiclesOf = (id) => vehicles.filter((v) => v.driver_id === id).map((v) => v.registration);

  return (
    <div className="p-8" data-testid="drivers-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">DRIVERS</h1>
          <p className="text-muted-foreground text-sm mt-1">{drivers.length} drivers</p>
        </div>
        {editable && (
        <Button data-testid="add-driver-button" onClick={openAdd} className="rounded-none bg-[#FF3B30] hover:bg-[#ff554b] font-bold uppercase tracking-widest text-xs h-10">
          <Plus size={15} className="mr-1" /> Add driver
        </Button>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="bg-card border-border rounded-none text-foreground max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading font-black tracking-tighter text-2xl">{editId ? "EDIT DRIVER" : "ADD DRIVER"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              {FIELDS.map(([label, key, type, tid]) => (
                <div key={key} className={key === "name" || key === "address" || key === "notes" ? "col-span-2" : ""}>
                  <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">{label}</Label>
                  <Input data-testid={tid} type={type} value={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} className="mt-1.5 rounded-none bg-background border-border h-9" />
                </div>
              ))}
              <div className="col-span-2">
                <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">Driving licence (PDF or photo)</Label>
                <label className="mt-1.5 flex items-center gap-2 border border-border px-3 py-2.5 text-xs cursor-pointer hover:bg-muted transition-colors duration-150">
                  <Upload size={14} /> {licFile ? licFile.name : "Choose file…"}
                  <input data-testid="driver-licence-file-input" type="file" accept="application/pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => setLicFile(e.target.files?.[0] || null)} />
                </label>
              </div>
              <Button data-testid="save-driver-button" onClick={save} disabled={saving} className="col-span-2 w-full rounded-none bg-[#FF3B30] hover:bg-[#ff554b] font-bold uppercase tracking-widest text-xs h-11">{saving ? "Saving…" : editId ? "Save changes" : "Save driver"}</Button>
            </div>
          </DialogContent>
        </Dialog>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {drivers.map((d) => (
          <div key={d.id} data-testid={`driver-card-${d.name}`} className="border border-border bg-card p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-heading font-black text-xl tracking-tighter">{d.name}</div>
                {d.position && <div className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground mt-0.5">{d.position}</div>}
              </div>
              <div className="flex items-center gap-2">
                {d.points > 0 && <span data-testid={`driver-points-${d.name}`} className="bg-[#FF3B30] text-white text-[10px] font-bold px-1.5 py-0.5">{d.points} pts</span>}
                {editable && (
                <>
                <button data-testid={`edit-driver-${d.name}`} onClick={() => openEdit(d)} className="text-muted-foreground/70 hover:text-foreground transition-colors duration-150">
                  <Pencil size={15} />
                </button>
                <button data-testid={`delete-driver-${d.name}`} onClick={() => remove(d)} className="text-muted-foreground/70 hover:text-[#FF3B30] transition-colors duration-150">
                  <Trash2 size={15} />
                </button>
                </>
                )}
              </div>
            </div>
            <div className="mt-3 space-y-1.5 text-sm text-muted-foreground">
              {d.phone && <div className="flex items-center gap-2"><Phone size={13} /> {d.phone}</div>}
              {d.email && <div className="flex items-center gap-2"><Mail size={13} /> {d.email}</div>}
              {(d.address || d.postcode) && <div className="flex items-center gap-2"><MapPin size={13} /> {[d.address, d.postcode].filter(Boolean).join(", ")}</div>}
              {d.licence_number && <div className="flex items-center gap-2"><IdCard size={13} /> <span className="font-mono text-xs">{d.licence_number}</span>{d.licence_country && <span className="text-xs">({d.licence_country})</span>}</div>}
              {d.licence_expiry && <div className={`flex items-center gap-2 ${expiryClass(d.licence_expiry)}`}><CalendarClock size={13} /> Licence exp {fmtDate(d.licence_expiry)}</div>}
              {d.cpc_expiry && <div className={`flex items-center gap-2 ${expiryClass(d.cpc_expiry)}`}><CalendarClock size={13} /> CPC exp {fmtDate(d.cpc_expiry)}</div>}
              {d.tacho_expiry && <div className={`flex items-center gap-2 ${expiryClass(d.tacho_expiry)}`}><CalendarClock size={13} /> Tacho exp {fmtDate(d.tacho_expiry)}</div>}
              {d.last_check_date && <div className="flex items-center gap-2 text-xs">Last check {fmtDate(d.last_check_date)}</div>}
              {d.notes && <div className="text-xs text-amber-600 dark:text-[#FFD60A]">{d.notes}</div>}
            </div>
            <div className="mt-3 flex items-center gap-3">
              {d.licence_file_name ? (
                <button data-testid={`view-licence-${d.name}`} onClick={() => viewLicence(d)} className="inline-flex items-center gap-1.5 text-xs text-emerald-600 dark:text-[#32D74B] hover:underline">
                  <FileText size={13} /> Licence
                </button>
              ) : (
                <span className="text-xs text-muted-foreground/70">No licence file</span>
              )}
              {editable && (
              <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground cursor-pointer">
                <Upload size={13} /> {d.licence_file_name ? "Replace" : "Upload"}
                <input data-testid={`upload-licence-${d.name}`} type="file" accept="application/pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { uploadLicence(d.id, e.target.files?.[0]); e.target.value = ""; }} />
              </label>
              )}
            </div>
            <div className="mt-4 pt-3 border-t border-border">
              <div className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground mb-1.5">Assigned vehicles</div>
              <div className="flex flex-wrap gap-1.5">
                {vehiclesOf(d.id).length === 0 && <span className="text-xs text-muted-foreground/70">None</span>}
                {vehiclesOf(d.id).map((reg) => (
                  <span key={reg} className="font-mono text-xs font-bold bg-muted/60 border border-border px-2 py-0.5">{reg}</span>
                ))}
              </div>
            </div>
          </div>
        ))}
        {drivers.length === 0 && (
          <div className="col-span-full border border-border bg-card p-12 text-center text-muted-foreground/70">No drivers yet. Click "Add driver" to get started.</div>
        )}
      </div>
    </div>
  );
}
